import Select from "react-select";
import { MdFolder, MdSubdirectoryArrowRight } from "react-icons/md";

export default function ImportFolderSelect({
  folders,
  value,
  onChange,
  disabled,
  label,
  isDarkMode,
  portalTarget,
  centered = false,
  placeholder,
  emptyMessage,
  onMenuOpen,
}) {
  const background = isDarkMode ? "#202733" : "#fff";
  const foreground = isDarkMode ? "#f1f5f9" : "#0f172a";
  return (
    <Select
      aria-label={label}
      options={folders}
      value={
        folders.find((folder) => String(folder.id) === String(value)) || null
      }
      getOptionValue={(folder) => String(folder.id)}
      getOptionLabel={(folder) => folder.path}
      filterOption={(option, query) =>
        query
          .toLocaleLowerCase()
          .split(/[\s/]+/)
          .filter(Boolean)
          .every((word) => option.data.path.toLocaleLowerCase().includes(word))
      }
      onChange={(folder) => onChange(folder ? String(folder.id) : "")}
      isDisabled={disabled}
      isSearchable
      onMenuOpen={onMenuOpen}
      isClearable
      placeholder={placeholder || (disabled ? "Choose a store first" : "Search folders…")}
      noOptionsMessage={() =>
        emptyMessage || (folders.length
          ? "No matching folders or subfolders"
          : "This store has no folders")
      }
      menuPortalTarget={portalTarget}
      menuPosition="fixed"
      menuPlacement="auto"
      maxMenuHeight={280}
      formatOptionLabel={(folder, { context, inputValue }) =>
        context === "value" ? (
          <div className="flex min-w-0 items-center gap-2">
            {folder.depth > 0 ? (
              <MdSubdirectoryArrowRight
                aria-hidden="true"
                className="shrink-0 opacity-60"
              />
            ) : (
              <MdFolder
                aria-hidden="true"
                className="shrink-0 text-amber-500"
              />
            )}
            <div className="min-w-0 flex-1 text-left">
              {folder.depth > 0 ? (
                <>
                  <div
                    title={folder.name}
                    className="min-w-0 truncate whitespace-nowrap font-semibold text-emerald-700"
                  >
                    {folder.name}
                  </div>
                  {folder.parentPath && (
                    <div
                      title={folder.parentPath}
                      className="mt-0.5 min-w-0 truncate whitespace-nowrap text-[11px] opacity-70"
                    >
                      Inside: {folder.parentPath}
                    </div>
                  )}
                </>
              ) : (
                <div
                  title={folder.path}
                  className="min-w-0 truncate whitespace-nowrap"
                >
                  {folder.path}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              paddingLeft: inputValue ? 0 : Math.min(folder.depth || 0, 5) * 14,
            }}
          >
            <div className="flex items-center gap-2">
              {folder.depth > 0 ? (
                <MdSubdirectoryArrowRight
                  aria-hidden="true"
                  className="shrink-0 opacity-60"
                />
              ) : (
                <MdFolder
                  aria-hidden="true"
                  className="shrink-0 text-amber-500"
                />
              )}
              <span className="break-words font-medium">{folder.name}</span>
              <span className="ml-auto shrink-0 text-[10px] opacity-60">
                {folder.depth > 0 ? "Subfolder" : "Folder"}
              </span>
            </div>
            {folder.parentPath && (
              <div className="mt-1 break-words pl-6 text-xs opacity-60">
                {folder.parentPath}
              </div>
            )}
          </div>
        )
      }
      styles={{
        container: (base) => ({ ...base, minWidth: 0, fontSize: 14 }),
        control: (base, state) => ({
          ...base,
          minHeight: centered ? 44 : 38,
          backgroundColor: background,
          borderRadius: 8,
          borderColor: state.isFocused ? "#3b82f6" : "#94a3b866",
          opacity: state.isDisabled ? 0.5 : 1,
        }),
        valueContainer: (base) => ({
          ...base,
          textAlign: centered ? "center" : "left",
        }),
        input: (base) => ({ ...base, color: foreground }),
        singleValue: (base) => ({ ...base, color: foreground }),
        placeholder: (base) => ({
          ...base,
          color: isDarkMode ? "#94a3b8" : "#64748b",
        }),
        menuPortal: (base) => ({ ...base, zIndex: 90 }),
        menu: (base) => ({
          ...base,
          textAlign: "left",
          backgroundColor: background,
          color: foreground,
          minWidth: 260,
          maxWidth: "calc(100vw - 2rem)",
          right: 0,
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 12px 30px #0003",
        }),
        option: (base, state) => ({
          ...base,
          color: foreground,
          backgroundColor: state.isSelected
            ? isDarkMode
              ? "#1e40af"
              : "#dbeafe"
            : state.isFocused
              ? isDarkMode
                ? "#334155"
                : "#f1f5f9"
              : background,
          cursor: "pointer",
        }),
      }}
    />
  );
}
